<template>
   <div>
      <div
         v-if="assignedFlags.length > 0 && (assigneeId === profile.id || isCustomer || isRndig)"
         class="assigned-flags-wrapper"
         :class="{'assigned-flags-wrapper--resolved': allFlagsResolved}"
      >
         <h3 class="flags-title">Verification Results</h3>
         <div v-if="assignedFixFlags.length > 0" class="d-flex flex-column justify-content-center">
            <div class="d-flex assigned-fix-flags-title">
               <div class="d-flex flags-section-header">
                  <custom-icon icon="icon-flag-fix" />
                  <p class="assigned-flags-title-text">Must Fix ({{ assignedFixFlags.length }})</p>
               </div>
            </div>
            <ul>
               <li
                  v-for="fixFlag in assignedFixFlags"
                  :key="`fix-flag-${fixFlag.id}`"
                  class="flag-issue"
               >
                  {{ fixFlag.failureInstructions }}
               </li>
            </ul>
         </div>
         <div
            v-for="groupedFlag in Object.values(groupedVerifiedFlagsByOthers)"
            :key="`verified-by-${groupedFlag.verifiedBy}`"
            class="d-flex flex-column justify-content-center"
         >
            <div class="d-flex flags-section-header">
               <custom-icon icon="icon-flag-verified" />
               <p class="assigned-flags-title-text">Verified by {{ groupedFlag.verifiedBy }}</p>
            </div>
            <div class="d-flex flex-column justify-content-center verify-flag-list-wrapper">
               <div
                  v-for="verifiedFlag in groupedFlag.flags"
                  :key="`verify-flag-${verifiedFlag.id}`"
                  class="verify-flag-list-item"
               >
                  <b-form-checkbox
                     size="small"
                     class="verified-checkbox"
                     :disabled="true"
                     :checked="true"
                     :value="true"
                  ></b-form-checkbox>
                  <p class="flag-issue">{{ verifiedFlag.failureInstructions }}</p>
               </div>
            </div>
         </div>
         <div
            v-for="groupedUserFlag in Object.values(groupedVerifiedFlagsByUser)"
            :key="`verified-by-${groupedUserFlag.verifiedBy}`"
            class="d-flex flex-column justify-content-center"
         >
            <div class="d-flex flags-section-header">
               <custom-icon icon="icon-flag-verified" />
               <p class="assigned-flags-title-text">Verified by Me</p>
            </div>
            <div class="d-flex flex-column justify-content-center verify-flag-list-wrapper">
               <div
                  v-for="verifiedFlag in groupedUserFlag.flags"
                  :key="`verify-flag-${verifiedFlag.id}`"
                  class="verify-flag-list-item"
               >
                  <b-form-checkbox
                     size="small"
                     class="verified-checkbox"
                     :disabled="true"
                     :checked="true"
                     :value="true"
                  ></b-form-checkbox>
                  <p class="flag-issue">{{ verifiedFlag.failureInstructions }}</p>
               </div>
            </div>
         </div>
         <div
            v-if="assignedVerifyFlags.length > 0"
            class="d-flex flex-column justify-content-center"
         >
            <div class="d-flex flags-section-header">
               <custom-icon icon="icon-flag-verify" />
               <p class="assigned-flags-title-text">
                  Must Verify ({{ assignedVerifyFlags.length }})
               </p>
            </div>
            <div class="d-flex flex-column justify-content-center verify-flag-list-wrapper">
               <div
                  v-for="verifyFlag in assignedVerifyFlags"
                  :key="`verify-flag-${verifyFlag.id}`"
                  class="verify-flag-list-item"
               >
                  <b-form-checkbox
                     size="small"
                     :id="`verify-flag-${verifyFlag.id}`"
                     :disabled="verifyFlag.verified"
                     v-model="flagsSelection[verifyFlag.id]"
                     :value="true"
                  ></b-form-checkbox>
                  <label :for="`verify-flag-${verifyFlag.id}`" class="flag-issue">{{
                     verifyFlag.failureInstructions
                  }}</label>
               </div>
               <b-button
                  :disabled="!anyFlagsSelected"
                  class="verify-selected-flags-btn"
                  variant="warning"
                  @click="verifySelectedFlags"
               >
                  Verify Selected items
               </b-button>
            </div>
         </div>
      </div>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {areAllFlagsResolved, getRelevantFlags} from '@/helpers/expenseCompletion';

export default {
   name: 'FlagsVerifications',
   props: {
      assigneeId: {
         type: String,
      },
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },
   data() {
      return {
         loaded: false,
         flagsSelection: {},
      };
   },
   computed: {
      ...mapGetters({
         profile: 'profile',
         isCustomer: 'isCustomer',
         activeStudyId: 'companies/activeStudyId',
         activeStudyPeriods: 'companies/activeStudyPeriods',
         studyPeriodMap: 'companies/studyPeriodMap',
         isRndig: 'isRndig',
         flags: 'flags/flags',
      }),

      periodId() {
         return this.$route.params.periodId;
      },

      assignedFlags() {
         return getRelevantFlags({
            flags: this.flags,
            period: this.period?.period,
            studyId: this.activeStudyId,
            checkSection: this.flagCheckSection,
            userId: this.assigneeId,
         });
      },

      /** All must fix assigned flags **/
      assignedFixFlags() {
         return this.assignedFlags.filter(
            (flag) => flag.severityLevel === this.$constants().FlagStates.MUST_FIX
         );
      },

      anyFlagsSelected() {
         return this.selectedFlagsIds.length > 0;
      },

      actionableFlagIds() {
         return this.assignedVerifyFlags.map((flag) => String(flag.id));
      },

      selectedFlagsIds() {
         const selectableIds = new Set(this.actionableFlagIds);

         return Object.entries(this.flagsSelection)
            .filter(([id, selected]) => selected && selectableIds.has(String(id)))
            .map(([id]) => id);
      },

      /** All must verify assigned flags **/
      assignedVerifyFlags() {
         return this.assignedFlags.filter(
            (flag) =>
               flag.severityLevel === this.$constants().FlagStates.MUST_VERIFY && !flag.verified
         );
      },

      /** All verified assigned flags **/
      assignedVerifiedFlags() {
         return this.assignedFlags.filter(
            (flag) =>
               flag.severityLevel === this.$constants().FlagStates.MUST_VERIFY && flag.verified
         );
      },

      /** All verified assigned flags grouped by other users verified **/
      groupedVerifiedFlagsByOthers() {
         const groupedFlags = {};

         const filteredVerifiedFlags = this.assignedVerifiedFlags.filter(
            (flag) => flag.verifiedByUserId !== this.profile.id
         );
         filteredVerifiedFlags.forEach((flag) => {
            if (groupedFlags[flag.verifiedByUserId]) {
               groupedFlags[flag.verifiedByUserId].flags.push(flag);
            } else {
               groupedFlags[flag.verifiedByUserId] = {flags: [flag], verifiedBy: flag.verifiedBy};
            }
         });

         return groupedFlags;
      },

      /** All verified assigned flags grouped by user current user **/
      groupedVerifiedFlagsByUser() {
         const groupedFlags = {};

         const filteredVerifiedFlags = this.assignedVerifiedFlags.filter(
            (flag) => flag.verifiedByUserId === this.profile.id
         );
         filteredVerifiedFlags.forEach((flag) => {
            if (groupedFlags[flag.verifiedByUserId]) {
               groupedFlags[flag.verifiedByUserId].flags.push(flag);
            } else {
               groupedFlags[flag.verifiedByUserId] = {flags: [flag], verifiedBy: flag.verifiedBy};
            }
         });

         return groupedFlags;
      },

      /** Checks whether all flags are verified and there are no must fix flags */
      allFlagsResolved() {
         return areAllFlagsResolved(this.assignedFlags, this.$constants().FlagStates);
      },

      period() {
         return this.studyPeriodMap[this.periodId];
      },

      flagCheckSection() {
         return this.$store.getters[`${this.expenseType}/flagCheckSection`];
      },
   },

   methods: {
      clearSelection() {
         this.flagsSelection = {};
      },

      async loadFlags() {
         // No period filter -- flags/loadFlags replaces the whole store slice on every
         // call (see flags.js's loadFlags action), and ExpensesCard.vue's own
         // hasUnresolvedFlags (feeding "Mark Section Complete") needs to see unresolved
         // flags across every period, not just this component's own. assignedFlags below
         // already narrows to this.period client-side for display.
         await this.$store.dispatch('flags/loadFlags', {
            companyId: this.$companyId,
            checkSection: this.flagCheckSection,
            studyId: this.activeStudyId,
         });
         this.clearSelection();
      },

      async verifySelectedFlags() {
         if (!this.selectedFlagsIds.length) {
            return;
         }

         await this.$store.dispatch('flags/verifyFlags', {
            flagIds: this.selectedFlagsIds,
            companyId: this.$companyId,
         });

         await this.loadFlags();
         this.$store.commit('companies/resetActiveStudyAssignment', {
            assigneeId: this.assigneeId,
            period: this.period.period,
            sectionName: this.expenseType,
         });
      },

      async loadData() {
         this.loaded = false;
         await this.loadFlags();
         this.loaded = true;
      },
   },

   watch: {
      // flags/loadFlags (called by loadData() below) already clears and replaces the
      // store's flags atomically once the fresh response lands (see flags.js) -- an
      // eager commit('flags/clear') here, before that fetch even starts, doesn't
      // protect against showing stale data; it briefly empties the GLOBAL (not
      // period-scoped) flags list that other pages' cross-period status checks also
      // read from, making every assignee look artificially "Complete" until the
      // re-fetch resolves. That produced a visible flicker on ExpensesCard.vue's
      // manager message when switching periods.
      async periodId(newPeriod, oldPeriod) {
         if (+newPeriod !== +oldPeriod) {
            await this.loadData();
         }
      },
   },

   async created() {
      await this.loadData();
   },
};
</script>

<style scoped lang="scss">
.assigned-flags-wrapper {
   background: #fff6000d;
   border-radius: 1.25rem;
   border: 1px solid #ffae004d;
   padding: 1.5rem 1rem;

   &--resolved {
      background: #22b56c08;
      border-color: #22b56c;
   }
}

.flags-title {
   font-size: 0.875rem;
}

.flags-section-header {
   gap: 0.5rem;
   align-items: baseline;
}

.assigned-flags-title-text {
   font-size: 0.75rem;
   font-weight: 600;
   color: black;
   margin: 0;
}

.verify-flag-list-wrapper {
   padding-left: 1.25rem;
}

.verify-flag-list-item {
   display: flex;
   align-items: baseline;
   gap: 0.375rem;
}

.flag-issue {
   font-size: 0.75rem;
   margin: 0;
}

.verify-selected-flags-btn {
   width: 8.75rem;
   margin-top: 1.25rem;
   font-size: 0.625rem;
}

::v-deep .verified-checkbox {
   label::after,
   label::before {
      background-color: #cccccc !important;
   }
}
</style>
